import _ from 'lodash';
import { Utils } from '../helpers/Utils';
import { IReferential } from '../interfaces/IReferential';
import { Order, OrderPart, OrderPartDTO } from '../interfaces/Order';
import { CaseCalculationPart, BuildOrderedCalculationPartsResult } from '../interfaces/Case';
import { OriginalPartShortNumberException, CalculationSectionCodeEnum } from '../helpers/Constants';
import { isNullOrUndefined } from 'util';

export const isPartAlreadyOrdered = (
  orderPart: OrderPart,
  calcPart: CaseCalculationPart,
  packageCalculationId: number | null
): boolean => {
  const guideMatch =
    orderPart.guideNumber === calcPart.guideNumber ||
    (orderPart.originalGuideNumber != null &&
      calcPart.originalGuideNumber != null &&
      orderPart.originalGuideNumber === calcPart.originalGuideNumber);
  const nameMatch = orderPart.name === calcPart.name || calcPart.name.startsWith(orderPart.name);
  return (
    (guideMatch && nameMatch) ||
    (packageCalculationId != null &&
      OriginalPartShortNumberException.includes(calcPart.repairMethod) &&
      orderPart.repairMethod === calcPart.repairMethod)
  );
};

export const convertCalculationPartsToOrderParts = (parts: CaseCalculationPart[]): OrderPart[] => {
  return parts
    .filter((part) => part.calculationSection?.code === CalculationSectionCodeEnum.PART)
    .map(
      (part) =>
        ({
          guideNumber: part.guideNumber,
          name: part.name,
          originalGuideNumber: part.originalGuideNumber ?? null,
          repairMethod: part.repairMethod,
          alternativeGuideNumber: part.alternativeGuideNumber ?? null,
          alternativePartCode: part.alternativePartCode ?? null,
          alternativePartDiscount: part.alternativePartDiscount ?? null,
          alternativePartName: part.alternativePartName ?? null,
          alternativePartQualityId: part.alternativePartQualityId ?? null,
          alternativePartRemarks: part.alternativePartRemarks ?? null,
          alternativePartStockLocation: part.alternativePartStockLocation ?? null,
          alternativePartStockQuantity: part.alternativePartStockQuantity ?? null,
          alternativePrice: part.alternativePrice ?? null,
          carPartCategoryId: part.carPartCategoryId ?? null,
          codeType: part.codeType ?? null,
          deliveryTime: part.deliveryTime ?? null,
          excludeFromOrder: part.excludeFromOrder ?? false,
          originalPrice: part.originalPrice,
          pieceNr: part.pieceNr,
          producer: part.producer ?? null,
          remarks: part.remarks ?? null,
          supplierId: part.supplierId ?? null,
          type: part.type ?? null,
          validityTime: part.validityTime ?? null,
          orderCode: part.orderCode ?? null,
          locationId: part.locationId ?? null,
          productId: part.productId ?? null,
          codEan: part.codEan ?? null,
          groupedUniqueId: part.groupedUniqueId ?? null,
          isDeleted: false
        } as OrderPart)
    );
};

export const buildOrderedCalculationParts = (
  caseCalculationParts: CaseCalculationPart[],
  orderPartList: OrderPart[],
  packageCalculationId: number | null,
  useMaintenanceOperationsTransfer: boolean
): BuildOrderedCalculationPartsResult => {
  const caseCalculationPartsCopy = _.cloneDeep(caseCalculationParts);
  const calculationPartsOrdered: CaseCalculationPart[] = [];

  for (let i = 0; i < caseCalculationPartsCopy.length; i++) {
    if (caseCalculationPartsCopy[i].calculationSection!.code === CalculationSectionCodeEnum.PART) {
      if (packageCalculationId && !useMaintenanceOperationsTransfer) {
        caseCalculationPartsCopy[i].isNew = true;
      }
      const orderPart = _.find(orderPartList, (op) =>
        isPartAlreadyOrdered(op, caseCalculationParts[i], packageCalculationId)
      );

      if (orderPart) {
        caseCalculationPartsCopy[i].isNew = false;
        caseCalculationPartsCopy[i].isOrdered = true;
        caseCalculationPartsCopy[i].alternativeGuideNumber = orderPart.alternativeGuideNumber;
        caseCalculationPartsCopy[i].alternativePartCode = orderPart.alternativePartCode;
        caseCalculationPartsCopy[i].alternativePartDiscount = orderPart.alternativePartDiscount;
        caseCalculationPartsCopy[i].alternativePartName = orderPart.alternativePartName;
        caseCalculationPartsCopy[i].alternativePartQualityId = orderPart.alternativePartQualityId;
        caseCalculationPartsCopy[i].alternativePartRemarks = orderPart.alternativePartRemarks;
        caseCalculationPartsCopy[i].alternativePartStockLocation =
          orderPart.alternativePartStockLocation;
        caseCalculationPartsCopy[i].alternativePartStockQuantity =
          orderPart.alternativePartStockQuantity;
        caseCalculationPartsCopy[i].alternativePrice = orderPart.alternativePrice;
        caseCalculationPartsCopy[i].alternativePartDiscount = orderPart.alternativePartDiscount;
        caseCalculationPartsCopy[i].carPartCategoryId = orderPart.carPartCategoryId;
        caseCalculationPartsCopy[i].codeType = orderPart.codeType;
        caseCalculationPartsCopy[i].deliveryTime = orderPart.deliveryTime;
        caseCalculationPartsCopy[i].discount = orderPart.discount ?? 0;
        caseCalculationPartsCopy[i].excludeFromOrder = orderPart.excludeFromOrder;
        caseCalculationPartsCopy[i].guideNumber = orderPart.guideNumber;
        caseCalculationPartsCopy[i].name = orderPart.name;
        caseCalculationPartsCopy[i].originalPrice = orderPart.originalPrice;
        caseCalculationPartsCopy[i].pieceNr = orderPart.pieceNr;
        caseCalculationPartsCopy[i].producer = orderPart.producer;
        caseCalculationPartsCopy[i].remarks = orderPart.remarks;
        caseCalculationPartsCopy[i].supplierId = orderPart.supplierId;
        caseCalculationPartsCopy[i].type = orderPart.type;
        caseCalculationPartsCopy[i].validityTime = orderPart.validityTime;
        caseCalculationPartsCopy[i].orderCode = orderPart.orderCode;
        caseCalculationPartsCopy[i].locationId = orderPart.locationId;
        caseCalculationPartsCopy[i].productId = orderPart.productId;
        caseCalculationPartsCopy[i].codEan = orderPart.codEan;
        caseCalculationPartsCopy[i].groupedUniqueId = orderPart.groupedUniqueId;
        calculationPartsOrdered.push(caseCalculationPartsCopy[i]);
      } else {
        if (orderPartList.length > 0) {
          caseCalculationPartsCopy[i].isNew = true;
        }
      }
    }
  }

  return { caseCalculationPartsCopy, calculationPartsOrdered };
};

export const pretUnitarFormat = (
  part: OrderPart | OrderPartDTO | undefined,
  pretOriginal: number,
  currencyCode: string,
  country: IReferential | null
) => {
  if (part!.alternativePartQualityId === null) {
    return (
      Utils.countryNumberFormat((Math.round(pretOriginal * 100) / 100).toFixed(2), country) +
      ' ' +
      (!isNullOrUndefined(currencyCode) && currencyCode !== '' ? currencyCode : '')
    );
  } else {
    if (isNullOrUndefined(part!.alternativePrice)) {
      return 0;
    }
    return (
      Utils.countryNumberFormat(
        (Math.round(part!.alternativePrice * 100) / 100).toFixed(2),
        country
      ) +
      ' ' +
      (!isNullOrUndefined(currencyCode) && currencyCode !== '' ? currencyCode : '')
    );
  }
};

export const getOrderParts = (orderList: Order[]): OrderPart[] => {
  return _.flatMap(orderList, (order: Order) =>
    _.filter(order.orderParts ?? [], (orderPart: OrderPart) => !orderPart.isDeleted)
  );
};
